import React, { isValidElement, Children, useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'

import FieldLayout from '../FieldLayout'
import { FormContext } from '../Form'
import FormField from '../FormField'
import { renderLabel, renderInfo, renderDescription } from '../renderFieldBase'
import type { IFieldConfig } from '../types'
import { useConfig } from '../useConfig'

const collectConfigs = (node: React.ReactNode) => {
  const collectedConfigs: IFieldConfig[] = []

  Children.forEach(node, (child) => {
    if (!isValidElement(child)) {
      return
    }
    if (child.type === FormField) {
      if (child.props && 'config' in child.props) {
        collectedConfigs.push(child.props.config)
      }
    } else if (child.props && child.props.children) {
      const result = collectConfigs(child.props.children)
      collectedConfigs.push(...result)
    }
  })

  return collectedConfigs
}

interface IFieldGroupProps {
  children?: React.ReactNode
  config: IFieldConfig
}

const FieldGroup: React.FC<IFieldGroupProps> = ({ config: initialConfig, children }) => {
  const configs = useMemo(() => collectConfigs(children), [children])
  const config = useConfig(initialConfig, configs)
  const formSize = useContextSelector(FormContext, ({ size }) => size)
  const formLayout = useContextSelector(FormContext, ({ layout }) => layout)

  const { layout: configLayout, size: configSize, layoutModifiers, hidden } = config
  const label = useMemo(() => renderLabel(config, configs), [config, configs])
  const info = useMemo(() => renderInfo(config, configs), [config, configs])
  const description = useMemo(() => renderDescription(config, configs), [config, configs])
  const layout = configLayout || formLayout
  const size = configSize || formSize

  if (hidden) {
    return null
  }

  return (
    <FieldLayout
      className={config.className}
      Component={config.layoutRender}
      description={description}
      info={info}
      label={label}
      layout={layout}
      layoutModifiers={layoutModifiers}
      size={size}
    >
      {children}
    </FieldLayout>
  )
}

export default React.memo(FieldGroup) as typeof FieldGroup
